import { useEffect, useState } from "react";
import { repoErrorMessage } from "@/components/repo-lifecycle/lifecycleVocab";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForkTemplate } from "@/api/mutations/repoScaffold";
import { useGiteaSeeds } from "@/api/mutations/gitea";
import { useManagedRepos } from "@/api/queries/gitContent";
import { useSettingsProfiles } from "@/api/queries/settingsProfiles";

// #276 (UC-4): „Workspace-Vorlage anlegen" — entweder aus einer mitgelieferten Quelle oder
// als Kopie einer bestehenden Vorlage. Optional gleich mit einem Agenten-Profil verknüpft,
// damit die Vorlage nicht unbenutzt herumsteht.
//
// Bewusst KEIN „überschreiben": das wäre Löschen + Neuanlegen und nähme Entwürfe, Versionen
// und Historie mit. Dafür bleibt der bestehende Seed-Weg.

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (managedRepoId: string) => void;
}

const EMPTY = {
  repoName: "",
  fromKind: "seed" as "seed" | "template",
  source: "",
  repoId: "",
  description: "",
  profileMode: "none" as "none" | "attach" | "create",
  profileId: "",
  profileName: "",
};

export function CreateTemplateDialog({ open, onOpenChange, onCreated }: Props) {
  const [form, setForm] = useState({ ...EMPTY });
  const [error, setError] = useState<string | null>(null);
  const mutation = useForkTemplate();
  const seeds = useGiteaSeeds(open);
  const repos = useManagedRepos();
  const profiles = useSettingsProfiles();
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const vorlagen = (repos.data ?? []).filter((r) => r.kind === "template");

  // Vorbelegen, sobald die Listen da sind — ein leeres Pflichtfeld, das man erst aufklappen
  // muss, ist eine unnötige Stolperstelle.
  useEffect(() => {
    if (!form.source && seeds.data && seeds.data.length > 0) set({ source: seeds.data[0].name });
  }, [seeds.data, form.source]);
  useEffect(() => {
    if (!form.repoId && vorlagen.length > 0) set({ repoId: vorlagen[0].id });
  }, [vorlagen.length, form.repoId]);

  function close(o: boolean) {
    if (!o) {
      setForm({ ...EMPTY });
      setError(null);
    }
    onOpenChange(o);
  }

  async function submit() {
    setError(null);
    const repoName = form.repoName.trim();
    if (!repoName) {
      setError("Ein Name ist erforderlich.");
      return;
    }
    const from =
      form.fromKind === "seed"
        ? form.source
          ? ({ kind: "seed", source: form.source } as const)
          : null
        : form.repoId
          ? ({ kind: "template", repoId: form.repoId } as const)
          : null;
    if (!from) {
      setError(form.fromKind === "seed" ? "Wähle eine Quelle." : "Wähle eine Vorlage.");
      return;
    }
    if (form.profileMode === "attach" && !form.profileId) {
      setError("Wähle das Profil, mit dem die Vorlage verknüpft werden soll.");
      return;
    }
    if (form.profileMode === "create" && !form.profileName.trim()) {
      setError("Das neue Profil braucht einen Namen.");
      return;
    }

    try {
      const res = await mutation.mutateAsync({
        repoName,
        from,
        description: form.description.trim() || undefined,
        profile:
          form.profileMode === "none"
            ? undefined
            : form.profileMode === "attach"
              ? { mode: "attach", profileId: form.profileId }
              : { mode: "create", name: form.profileName.trim() },
      });
      toast.success(`Vorlage „${repoName}" angelegt`, { description: res.repoFullName });
      onCreated?.(res.managedRepoId);
      close(false);
    } catch (err) {
      // EINE Fehlercode-Tabelle für alle Repo-Dialoge (lifecycleVocab) — vorher hatte
      // jeder Dialog eine eigene Ternary-Kette mit teils anderen Sätzen für dieselben
      // Codes (Review-Fund #8: sechs driftende Tabellen).
      setError(repoErrorMessage(err, "Anlegen fehlgeschlagen"));
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="dialog-create-template">
        <DialogHeader>
          <DialogTitle>Workspace-Vorlage anlegen</DialogTitle>
          <DialogDescription>
            Eine Vorlage bestimmt, womit der Arbeitsbereich eines neuen Agenten startet.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="tpl-name">Name</Label>
            <Input
              id="tpl-name"
              value={form.repoName}
              onChange={(e) => set({ repoName: e.target.value })}
              placeholder="team-workspace"
              data-testid="input-template-name"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="tpl-from">Womit anfangen?</Label>
            <Select value={form.fromKind} onValueChange={(v) => set({ fromKind: v as "seed" | "template" })}>
              <SelectTrigger id="tpl-from" data-testid="select-template-from">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="seed">Mitgelieferte Quelle</SelectItem>
                <SelectItem value="template">Kopie einer bestehenden Vorlage</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.fromKind === "seed" ? (
            <div className="space-y-1.5">
              <Label htmlFor="tpl-source">Quelle</Label>
              <Select value={form.source} onValueChange={(v) => set({ source: v })}>
                <SelectTrigger id="tpl-source" data-testid="select-template-source">
                  <SelectValue placeholder="Quelle wählen" />
                </SelectTrigger>
                <SelectContent>
                  {(seeds.data ?? []).map((s) => (
                    <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Ein Ladefehler wird BENANNT, nicht als leere Auswahl gerendert — sonst
                  sieht „nicht erreichbar" aus wie „es gibt keine Quellen". */}
              {seeds.isError && (
                <p className="text-[12px] text-danger">
                  Quellen nicht ladbar: {(seeds.error as Error).message}
                </p>
              )}
              {!seeds.isError && seeds.data?.length === 0 && (
                <p className="text-[12px] text-text3">Keine mitgelieferten Quellen vorhanden.</p>
              )}
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="tpl-repo">Vorlage</Label>
              <Select value={form.repoId} onValueChange={(v) => set({ repoId: v })}>
                <SelectTrigger id="tpl-repo" data-testid="select-template-repo">
                  <SelectValue placeholder="Vorlage wählen" />
                </SelectTrigger>
                <SelectContent>
                  {vorlagen.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.displayName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {repos.isError && (
                <p className="text-[12px] text-danger">
                  Vorlagen nicht ladbar: {(repos.error as Error).message}
                </p>
              )}
              {!repos.isError && vorlagen.length === 0 && (
                <p className="text-[12px] text-text3">Es gibt noch keine Vorlage zum Kopieren.</p>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="tpl-profile">Mit einem Agenten-Profil verknüpfen?</Label>
            <Select
              value={form.profileMode}
              onValueChange={(v) => set({ profileMode: v as "none" | "attach" | "create" })}
            >
              <SelectTrigger id="tpl-profile" data-testid="select-template-profile-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nein, nur die Vorlage</SelectItem>
                <SelectItem value="attach">Ja, mit einem bestehenden Profil</SelectItem>
                <SelectItem value="create">Ja, neues Profil anlegen</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.profileMode === "attach" && (
            <div className="space-y-1.5">
              <Label htmlFor="tpl-profile-id">Profil</Label>
              <Select value={form.profileId} onValueChange={(v) => set({ profileId: v })}>
                <SelectTrigger id="tpl-profile-id" data-testid="select-template-profile">
                  <SelectValue placeholder="Profil wählen" />
                </SelectTrigger>
                <SelectContent>
                  {(profiles.data ?? []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {form.profileMode === "create" && (
            <div className="space-y-1.5">
              <Label htmlFor="tpl-profile-name">Name des neuen Profils</Label>
              <Input
                id="tpl-profile-name"
                value={form.profileName}
                onChange={(e) => set({ profileName: e.target.value })}
                placeholder="Team-Standard"
                data-testid="input-template-profile-name"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="tpl-desc">Beschreibung (optional)</Label>
            <Input
              id="tpl-desc"
              value={form.description}
              onChange={(e) => set({ description: e.target.value })}
              placeholder="Wofür ist diese Vorlage gedacht?"
            />
          </div>

          {error && (
            <p className="text-[13px] text-danger" data-testid="create-template-error">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={mutation.isPending}>
            Abbrechen
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={mutation.isPending || !form.repoName.trim()}
            data-testid="submit-create-template"
          >
            {mutation.isPending ? "Lege an …" : "Anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
